import React from 'react';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { NoData, Poppins, Spacer, TableSkeleton, getIncompleteStateNew } from 'src/common';
import { Modifier } from 'src/components/Modifier';
import Button from 'src/components/form/Button';
import { getPercentage } from 'src/utils/misc';
import styled from 'styled-components';
import { useColumnsControls } from './useColumnsControls';
import GenericTable from 'src/components/GenericTable';
import useVersion from 'src/hooks/useVersion';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { AiSuggest } from '../../../comps/AiSuggest';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import { convertToModifiers, convertToModifiersGql } from 'src/components/ProjectedRisk/util';
import { isQueryLoading, useCache } from 'src/graphql';
import { ScenarioQuery, useScenarioControlsQuery } from 'src/graphql/_generated';
import { RiskManagementModal } from 'src/components/modals';

const Div = styled.div`
  .head {
    display: flex;
    justify-content: space-between;
  }

  .empty-label {
    font-size: 18px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.cflowerBlue};
  }

  .error-label {
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.gp1};
  }
`;

interface ControlsProps {
  scenario: NonNullable<ScenarioQuery['scenario']>;
}

export const Controls: React.FC<ControlsProps> = ({ scenario }) => {
  const { version } = useVersion();
  const { setLinkControlsToScenarioModal } = useActionsApp();
  const { refetchQuery } = useCache();

  const {
    data: scenarioControlsData,
    loading,
    networkStatus,
    error,
  } = useScenarioControlsQuery({
    variables: {
      id: scenario.id,
      versionId: version,
    },
  });

  const isLoading = isQueryLoading({
    loading,
    data: scenarioControlsData,
    networkStatus,
  });

  const riskManagementPlans = scenarioControlsData?.scenario?.riskManagementPlans || [];

  const { columns, GTColumnSelectAnchorExported, editModalId, setEditModalId } = useColumnsControls({
    data: riskManagementPlans,
    scenario: scenario,
  });

  const resetCache = async () => refetchQuery({ include: ['scenario', 'scenarioControls'] });

  const isSuggestionButtonReadOnly = !!scenario.riskManagementPlans?.length;

  return (
    <>
      {editModalId && (
        <RiskManagementModal
          onClose={() => {
            resetCache();
            setEditModalId(undefined);
          }}
          controlId={editModalId}
        />
      )}
      <Div>
        <div className="head">
          <Poppins weight={600}>RISK MANAGEMENT PLAN</Poppins>
          {!version && (
            <Button
              primary
              onClick={() => {
                setLinkControlsToScenarioModal(true);
                mpEvent(MPEvents.ButtonClick, {
                  button: 'Add controls',
                  tags: ['SCENARIO', 'RMP'],
                });
              }}
              data-cy="add-controls-btn"
              $constWidth={200}
            >
              + ADD CONTROLS
            </Button>
          )}
        </div>
        {!isLoading ? (
          <>
            {!isSuggestionButtonReadOnly && (
              <AiSuggest
                wrapperStyle={{
                  marginTop: -10,
                  marginBottom: !!riskManagementPlans.length ? 4 : 10,
                }}
                scenarioId={scenario.id}
              />
            )}
            {riskManagementPlans.length ? (
              <>
                <Spacer $px={10} />
                <GenericTable
                  tableId={TableIds.scenarioControls}
                  data={riskManagementPlans}
                  columns={columns}
                  GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
                  searchable={[
                    'strategy',
                    'name',
                    'owner',
                    'frameworkLibrary_shortname',
                    'controlId',
                    'status',
                    'updated_at',
                  ]}
                  expandContent={(rmp) => {
                    const control = rmp.control;
                    if (!control || getIncompleteStateNew(scenario) === 'TECH') return null;

                    const recommendedModifiers = rmp.recommendedModifiers
                      ? convertToModifiers(rmp.recommendedModifiers)
                      : null;

                    return (
                      <Modifier
                        projectedScenario={{
                          id: scenario.id,
                          upper: scenario.baseRisk?.upper || 0,
                          lower: scenario.baseRisk?.lower || 0,
                          frequency_years: scenario.baseRisk?.frequencyYears || 0,
                          frequency_times: getPercentage(
                            scenario.baseRisk?.frequencyTimes,
                            scenario.baseRisk?.frequencyYears,
                          ),
                        }}
                        startingValues={{
                          frequency_times: (rmp.startingFrequency || 0) * 100,
                          lower: rmp.startingLower || 0,
                          upper: rmp.startingUpper || 0,
                        }}
                        disabled={!!version}
                        control={{
                          ...control,
                          modifiers: convertToModifiersGql(rmp.modifiers || []),
                        }}
                        onSave={resetCache}
                        withCopy
                        recommendedModifiers={recommendedModifiers}
                      />
                    );
                  }}
                />
              </>
            ) : (
              <>
                <Spacer $px={8} />
                {error ? <span className="error-label">Server error</span> : <NoData title="No controls linked" />}
              </>
            )}
          </>
        ) : (
          <TableSkeleton />
        )}
      </Div>
    </>
  );
};
